import { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSubmitOnChange } from '../../UI';
import { PersonalPropertyItemFormSubmitData } from '../types';
import { PersonalPropertyItem } from '../../generated/graphql';
import { usePersonalPropertyItem } from '../queries';
import { getPersonalPropertyItemFormDefaultValues } from '../../UI/components/PersonalPropertyItemForm/util/getPersonalPropertyItemFormDefaultValues';
import { PersonalPropertyItemFormValues } from '../../UI';

export interface UsePersonalPropertyItemFormArgs {
  householdID: string;
  saveOnChange?: boolean;
  personalPropertyItemID?: string | null;
  defaultValueOverrides?: Partial<PersonalPropertyItemFormValues>;
  handleSubmit?: (data: PersonalPropertyItemFormSubmitData) => void;
  personalPropertyItem?: PersonalPropertyItem | null;
  isLoading?: boolean;
}

export const usePersonalPropertyItemForm = ({
  householdID,
  saveOnChange,
  handleSubmit,
  personalPropertyItemID,
  defaultValueOverrides,
  personalPropertyItem: personalPropertyItemProp,
  isLoading = false,
}: UsePersonalPropertyItemFormArgs) => {
  const { personalPropertyItem: personalPropertyItemFromQuery, personalPropertyItemQuery } = usePersonalPropertyItem({
    householdID,
    id: personalPropertyItemID ?? '',
  });

  const personalPropertyItem = personalPropertyItemProp ?? personalPropertyItemFromQuery;
  const isLoadingFormData = personalPropertyItemQuery.isLoading || isLoading;
  const formReady = !isLoadingFormData && (personalPropertyItem || !personalPropertyItemID);

  const formMethods = useForm<PersonalPropertyItemFormValues>({
    // @ts-expect-error TODO: resolve type narrowing/widening with form values to support async entity loading
    values: getPersonalPropertyItemFormDefaultValues(personalPropertyItem, defaultValueOverrides),
  });

  const onSubmit: SubmitHandler<PersonalPropertyItemFormValues> = useCallback(
    (data) => {
      handleSubmit?.({
        formValues: data,
        householdID,
        changeToken: personalPropertyItem?.changeToken,
        personalPropertyItemID: personalPropertyItemID || undefined,
        valueDataPointGroupID: personalPropertyItem?.value.id || '',
      });
    },
    [
      personalPropertyItemID,
      handleSubmit,
      householdID,
      personalPropertyItem?.changeToken,
      personalPropertyItem?.value.id,
    ]
  );

  useSubmitOnChange({
    onChange: onSubmit,
    watch: formMethods.watch,
    enabled: saveOnChange && !!personalPropertyItem,
    handleSubmit: formMethods.handleSubmit,
  });

  return { formMethods, onSubmit, isLoadingFormData, formReady, personalPropertyItem, householdID };
};
